// Third-party
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';

// Actions
import { getDataUploadPortalConfigs, triggerDataUploadAction } from '@/redux/modules/enterprise/admin/data-upload';

// Components
import AdminDataPortalHeader from './AdminDataPortalHeader';
import EnterpriseDataUpload from './EnterpriseDataUpload';
import GroupDataUpload from './GroupDataUpload';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';

// Types
import {
  DataUploadActionKeys,
  DataUploadPortalConfig,
  DataUploadPortalFile,
  DataUploadPortalGroupType,
  DataUploadPortalStatus,
  DataUploadPortalStep,
} from '@/types/data-upload-config';
import { ReduxState } from '@/redux/modules/types';

// Utils
import notification from '@/utils/notification';
import { formatMessage } from '@/utils/i18n';

type DispatchProps = {
  configs: Array<DataUploadPortalConfig>;
  getDataUploadPortalConfigs: () => Promise<Array<DataUploadPortalConfig>>;
  triggerDataUploadAction: (configKey: string, actionKey: string) => Promise<void>;
};

type Props = DispatchProps & {};

const AdminDataPortal: React.FC<Props> = (props: Props) => {
  const { configs, getDataUploadPortalConfigs, triggerDataUploadAction } = props;

  const [enterpriseData, setEnterpriseData] = useState<Array<DataUploadPortalConfig>>([]);
  const [groupData, setGroupData] = useState<Array<DataUploadPortalConfig>>([]);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<DataUploadPortalConfig>();
  const [failedItems, setFailedItems] = useState<number>(0);

  const validateFailedImports = useCallback(
    async (data: DataUploadPortalConfig[]) => {
      const failedKeys = data?.reduce((acc, config: DataUploadPortalConfig) => {
        if (config?.dataUpload?.status === DataUploadPortalStatus.FAILED) return [...acc, config.configKey as string];
        return acc;
      }, [] as string[]);

      if (failedKeys.length > failedItems) {
        notification.error(formatMessage('Admin.DataUpload.Status.ImportFailed.Description'), formatMessage('Admin.DataUpload.Status.ImportFailed.Title'));
      }

      setFailedItems(failedKeys.length);
    },
    [failedItems],
  );

  useEffect(() => {
    getDataUploadPortalConfigs();
    const interval = setInterval(getDataUploadPortalConfigs, 5000);
    return () => clearInterval(interval);
  }, [getDataUploadPortalConfigs]);

  useEffect(() => {
    filterDataByJobType(configs);
    validateFailedImports(configs);
  }, [configs, validateFailedImports]);

  const handleImportStatus = (type: DataUploadPortalGroupType | string, configKey: string, status: DataUploadPortalStatus, step?: DataUploadPortalStep) => {
    if (type === DataUploadPortalGroupType.Enterprise) {
      setEnterpriseData(prevState => prevState.map(obj => (obj.configKey === configKey ? { ...obj, dataUpload: { ...obj.dataUpload, status, step } } : obj)));
    }

    if (type === DataUploadPortalGroupType.Groups) {
      setGroupData(prevState => prevState.map(obj => (obj.configKey === configKey ? { ...obj, dataUpload: { ...obj.dataUpload, status, step } } : obj)));
    }
  };

  const handleImportFiles = (type: DataUploadPortalGroupType, configKey: string) => {
    if (!handleValidateMissingFiles(configKey)) return;
    return triggerDataUploadAction(configKey, DataUploadActionKeys.add)
      .then(() => handleImportStatus(type, configKey, DataUploadPortalStatus.ONGOING, DataUploadPortalStep.NEW))
      .catch(() => {
        handleImportStatus(type, configKey, DataUploadPortalStatus.FAILED, undefined);
        notification.error(formatMessage('Admin.DataUpload.Status.ImportFailed.Description'), formatMessage('Admin.DataUpload.Status.ImportFailed.Title'));
      });
  };

  const handleCancelImport = (type: DataUploadPortalGroupType | string, configKey: string) => {
    let selected;

    if (type === DataUploadPortalGroupType.Enterprise) {
      selected = enterpriseData.find(item => item.configKey === configKey);
    }

    if (type === DataUploadPortalGroupType.Groups) {
      selected = groupData.find(item => item.configKey === configKey);
    }

    setSelectedJob(selected);
    toggleCancelModal();
  };

  const handleConfirmCancelImport = () => {
    const type = selectedJob?.groups.targetedGroups.jobType;
    const configKey = selectedJob?.configKey;

    if (!type || !configKey) return;

    triggerDataUploadAction(configKey, DataUploadActionKeys.cancel)
      .then(() => handleImportStatus(type, configKey, DataUploadPortalStatus.CANCELED))
      .catch(error => {
        throw error;
      })
      .finally(() => toggleCancelModal());
  };

  const handleValidateMissingFiles = (configKey: string) => {
    const updatedItem = configs.find((config: DataUploadPortalConfig) => config.configKey === configKey);
    const uploadedFiles = updatedItem && updatedItem.files.filter((file: DataUploadPortalFile) => file.createdAt && file.url);

    if (updatedItem && uploadedFiles && uploadedFiles.length < updatedItem.files.length) {
      notification.error(formatMessage('Admin.DataUpload.Status.CannotProceed.MissingFiles'), formatMessage('Admin.DataUpload.Status.CannotProceed.Title'));
      return false;
    }
    return true;
  };

  const filterDataByJobType = (data: DataUploadPortalConfig[]) => {
    const enterpriseJobs = data.filter(item => item.groups.targetedGroups.jobType === DataUploadPortalGroupType.Enterprise);
    const groupsJobs = data.filter(item => item.groups.targetedGroups.jobType === DataUploadPortalGroupType.Groups);
    setEnterpriseData(enterpriseJobs);
    setGroupData(groupsJobs);
  };

  const toggleCancelModal = () => {
    setShowCancelModal(prevState => !prevState);
  };

  return (
    <>
      <div className="admin-enterprise admin-tab">
        <div className="admin-data-portal-content">
          <AdminDataPortalHeader />
          <EnterpriseDataUpload data={enterpriseData} onImportFiles={handleImportFiles} onCancelImport={handleCancelImport} />
          <GroupDataUpload data={groupData} onImportFiles={handleImportFiles} onCancelImport={handleCancelImport} />
        </div>
      </div>

      {showCancelModal && (
        <DestructiveModal
          open
          onOpenChange={open => !open && toggleCancelModal()}
          title={formatMessage('Admin.DataUpload.Status.CancelModal.Title')}
          content={formatMessage('Admin.DataUpload.Status.CancelModal.Body', { jobName: selectedJob?.name || '' })}
          confirmButtonText={formatMessage('Admin.DataUpload.Status.CancelModal.ConfirmButton')}
          cancelButtonText={formatMessage('Common.Cancel')}
          onCancel={toggleCancelModal}
          onConfirm={handleConfirmCancelImport}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  configs: state.dataUploadConfigs.configs,
});

export default connect(mapStateToProps, {
  getDataUploadPortalConfigs,
  triggerDataUploadAction,
})(AdminDataPortal);
